import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  ArrowUpCircle,
  Download,
  FileText,
  FolderTree,
  History,
  Info,
  Layers,
  Loader2,
  Plug,
  Puzzle,
  RotateCcw,
  ShieldCheck,
  SlidersHorizontal,
  Trash2,
  Wrench,
} from "lucide-react";

import { useViewScope } from "@/shared/hooks/use-view-scope";
import { useIsAdmin } from "@/shared/hooks/use-admin-validation";
import { useIsSuperAdminRole } from "@/shared/hooks/use-system-role";
import { useToast } from "@/shared/hooks/use-toast";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { LoadingSpinner } from "@/shared/components/ui/loading-spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { SkillsService, type SkillScopeTarget } from "../services/skillsService";
import { requirementLabel } from "../utils/requirement";
import { parseSkillFacets } from "../utils/facets";
import { textoDaSkill } from "../utils/text";
import { FilesStep, OverviewStep, ParametersStep } from "./SkillInstaller/steps";
import { SkillProvenance } from "./SkillProvenance";
import { invalidarSkills } from "../utils/invalidate";
import { SkillUpdateDialog } from "./SkillUpdateDialog";
import type { InstalledSkillDetail } from "../types/skill";

interface SkillSettingsProps {
  /**
   * Onde a skill está instalada. Quando ausente, sai da navegação — é o caso da
   * rota `/skills/:name/settings`. A área de perfil passa o alvo pessoal.
   */
  target?: SkillScopeTarget;
  /** Para onde voltar ao sair. Padrão: a tela de Skills da navegação atual. */
  backTo?: string;
}

/**
 * A tela de configuração de uma skill instalada (AB#85634, AC22 e AC32).
 *
 * **Cabeçalho de identidade e abas abaixo dele**, como a tela de perfil: quem é
 * a skill vale para todas as abas, e deixá-la dentro de uma faria a identidade
 * sumir a cada troca.
 *
 * As abas de conteúdo — o procedimento, a licença e a estrutura — reusam os
 * **mesmos componentes do assistente de instalação**. É deliberado: quem
 * configura precisa reler exatamente o que leu antes de instalar, e duas
 * renderizações do mesmo pacote divergiriam com o tempo.
 *
 * As duas abas próprias daqui são as que só existem depois de instalar: as
 * **versões** publicadas — o histórico do procedimento — e o **histórico** de
 * quem instalou e desinstalou, que é o histórico da decisão.
 */
export const SkillSettings = ({ target: alvoExplicito, backTo }: SkillSettingsProps) => {
  const { t, i18n } = useTranslation();
  const { name = "" } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { scope, productId, isResolving } = useViewScope();
  const { isAdmin } = useIsAdmin();
  const { isSuperAdmin } = useIsSuperAdminRole();

  const [pendente, setPendente] = useState(false);
  /**
   * Os parâmetros em edição.
   *
   * `null` significa "ainda não mexeu": a aba mostra o que está gravado. Depois
   * do primeiro toque, passa a mostrar o rascunho — senão cada tecla digitada
   * seria sobrescrita pela resposta da consulta.
   */
  const [rascunho, setRascunho] = useState<Record<string, string> | null>(null);
  /**
   * Para qual versão a prévia aberta aponta: `"latest"` é o botão de atualizar
   * do cabeçalho, um número é o de voltar de alguma linha da aba de versões, e
   * `null` é nenhuma prévia aberta. Um estado só porque é uma pergunta só —
   * para onde esta instalação vai.
   */
  const [alvoDaTroca, setAlvoDaTroca] = useState<number | "latest" | null>(null);

  // Escopo de plataforma não instala skill nenhuma; cair nele aqui é navegação
  // fora de lugar, e o alvo pessoal é o único que vem por prop.
  /**
   * O nível desta tela sai da navegação, como na grade (AB#85634, AC13).
   *
   * O espaço PESSOAL precisa de ramo próprio: cair no "senão" faria a tela
   * perguntar pela organização por uma skill instalada no espaço de alguém, e
   * responder "não está instalada aqui" sobre algo que está — a tela diria a
   * verdade sobre o lugar errado.
   */
  const target: SkillScopeTarget =
    alvoExplicito ??
    (scope === "product"
      ? { scope, productId }
      : scope === "user"
        ? { scope: "user" }
        : { scope: "organization" });

  const podeAdministrar = target.scope === "user" || isAdmin || isSuperAdmin;
  const chave = [target.scope, target.productId ?? "-", name];

  const {
    data: skill,
    isLoading,
    isError,
  } = useQuery<InstalledSkillDetail>({
    queryKey: ["skill_installed_detail", ...chave],
    enabled: !!name && !isResolving,
    queryFn: () => SkillsService.installedDetail(target, name),
  });

  /*
   * O conteúdo do pacote vem do CATÁLOGO, e não da instalação.
   *
   * Consequência que vale saber: uma instalação presa a uma versão antiga mostra
   * aqui o texto da versão corrente. Resolver isso exigiria ler o pacote por
   * versão, e a aba de Versões já diz quando as duas divergem — enquanto o
   * `resource` do catálogo serve a versão corrente, é o melhor que dá para
   * mostrar sem prometer o que a api ainda não entrega.
   */
  const { data: pacote } = useQuery({
    queryKey: ["skill_catalog_detail", ...chave],
    enabled: !!name && !isResolving,
    queryFn: () => SkillsService.catalogDetail(target, name),
  });

  const { data: versoes = [] } = useQuery({
    queryKey: ["skill_versions", ...chave],
    enabled: !!name && !isResolving,
    queryFn: () => SkillsService.versions(target, name),
  });

  const { data: historico = [] } = useQuery({
    queryKey: ["skill_history", ...chave],
    enabled: !!name && !isResolving,
    queryFn: () => SkillsService.history(target, name),
  });

  const voltar = () => navigate(backTo ?? "/skills");

  const agir = async (acao: () => Promise<unknown>, falha: string, depois?: () => void) => {
    setPendente(true);
    try {
      await acao();
      invalidarSkills(queryClient);
      depois?.();
    } catch (error) {
      toast({
        variant: "destructive",
        title: t(falha),
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setPendente(false);
    }
  };

  if (isResolving || isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError || !skill) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" className="gap-1" onClick={voltar}>
          <ArrowLeft className="h-4 w-4" />
          {t("skills.settings.back")}
        </Button>
        <p className="text-sm text-destructive">{t("skills.settings.notInstalled")}</p>
      </div>
    );
  }

  const etiquetas = parseSkillFacets(pacote?.facets);
  const versoesDaInstalacao = versoes.filter((versao) => versao.version <= skill.version);

  return (
    <div className="w-full animate-fade-in">
      <Button variant="ghost" size="sm" className="mb-4 gap-1" onClick={voltar}>
        <ArrowLeft className="h-4 w-4" />
        {t("skills.settings.back")}
      </Button>

      {/* Identidade: de quem é esta tela. Fica FORA das abas, porque vale para
          todas elas — dentro de uma, sumiria ao trocar de aba. */}
      <div className="flex items-start gap-5 pb-6">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 ring-4 ring-background">
          <Puzzle className="h-6 w-6 text-primary" />
        </div>

        <div className="min-w-0 flex-1 space-y-1">
          {/* A versão ao lado do título: ela qualifica a skill, e no canto
              direito ficava longe do que qualifica — lida como um estado da
              tela, e não como parte da identidade dela. */}
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-semibold">
              {textoDaSkill(skill.title, skill.title_i18n, i18n.language) || skill.name}
            </h2>
            <Badge variant="secondary">{t("skills.version", { version: skill.version })}</Badge>
          </div>
          <p className="font-mono text-xs text-muted-foreground/70">{skill.name}</p>
          {/* Título e descrição são complemento do Prodgy e têm idioma; o
              procedimento abaixo é o produto da skill e não tem (AC37). */}
          <p className="text-sm text-muted-foreground">
            {textoDaSkill(skill.description, skill.description_i18n, i18n.language)}
          </p>

          {/* As etiquetas, como no cartão do catálogo. Aqui aparecem TODAS, sem
              o "+N": o cartão as corta porque é uma célula de grade com largura
              de vizinho; esta é a página da skill, e esconder o que ela é para
              economizar espaço que sobra não serve a ninguém. */}
          {pacote && <SkillProvenance detail={pacote} className="pt-1" />}

          {etiquetas.length > 0 && (
            <div className="flex flex-wrap items-center gap-1 pt-1">
              {etiquetas.map((etiqueta) => (
                <Tooltip key={etiqueta.raw}>
                  <TooltipTrigger asChild>
                    <Badge variant="secondary" className="text-[11px] font-normal">
                      {etiqueta.value}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    {etiqueta.name}: {etiqueta.value}
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          )}
        </div>

        {/* Sobra a AÇÃO, que é o que merece o canto de ação. O selo de escopo
            saiu daqui: desde que cada visão mostra apenas as instalações do
            próprio nível (AC20), ele repetia onde a pessoa já está. */}
        <div className="flex shrink-0 flex-col items-end gap-2">
          {skill.available_version && podeAdministrar && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              disabled={pendente}
              // Abre a PRÉVIA em vez de atualizar: trocar de versão troca o
              // procedimento que o time validou, e fazer isso num clique é o
              // mesmo que trocar por baixo — muda só quem apertou o botão.
              onClick={() => setAlvoDaTroca("latest")}
            >
              {pendente ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ArrowUpCircle className="h-3.5 w-3.5" />
              )}
              {t("skills.update.action")}
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <div className="border-b">
          <TabsList>
            <TabsTrigger value="overview">
              <Info className="mr-2 h-4 w-4" />
              {t("skills.settings.tabs.overview")}
            </TabsTrigger>
            <TabsTrigger value="content">
              <FolderTree className="mr-2 h-4 w-4" />
              {t("skills.settings.tabs.content")}
            </TabsTrigger>
            <TabsTrigger value="integrations">
              <Plug className="mr-2 h-4 w-4" />
              {t("skills.settings.tabs.integrations")}
            </TabsTrigger>
            {/* Só quando a skill declara algum: aba vazia é promessa de
                configuração que não existe. */}
            {(skill.parameters?.length ?? 0) > 0 && (
              <TabsTrigger value="parameters">
                <SlidersHorizontal className="mr-2 h-4 w-4" />
                {t("skills.parameters.title")}
              </TabsTrigger>
            )}
            <TabsTrigger value="versions">
              <Layers className="mr-2 h-4 w-4" />
              {t("skills.settings.tabs.versions")}
            </TabsTrigger>
            <TabsTrigger value="history">
              <History className="mr-2 h-4 w-4" />
              {t("skills.settings.tabs.history")}
            </TabsTrigger>
            {podeAdministrar && (
              <TabsTrigger value="security">
                <ShieldCheck className="mr-2 h-4 w-4" />
                {t("skills.settings.tabs.security")}
              </TabsTrigger>
            )}
          </TabsList>
        </div>

        {/* Só o procedimento. O cartão de versão que ficava aqui saiu: a versão
            já está no cabeçalho, e repeti-la empurrava para baixo o conteúdo da
            aba. A troca de versão foi para o topo, ao lado do número — é lá que
            ela é lida, então é lá que ela se decide. */}
        <TabsContent value="overview" className="mt-6">
          {pacote && (
            <OverviewStep
              detail={pacote}
              showMeta={false}
              license={{
                cacheKey: [target.scope, target.productId ?? "-"],
                readFile: (path) => SkillsService.catalogResource(target, pacote.name, path),
              }}
              bodyClassName="max-h-[calc(100vh-24rem)] min-h-[420px]"
            />
          )}
        </TabsContent>

        {/* O editor ocupa a altura que recebe. Numa página não há altura a
            herdar, e sem esta medida ele colapsava para três linhas. */}
        <TabsContent value="content" className="mt-6">
          {pacote && (
            <FilesStep
              detail={pacote}
              cacheKey={[target.scope, target.productId ?? "-"]}
              readFile={(path) => SkillsService.catalogResource(target, pacote.name, path)}
              className="h-[calc(100vh-20rem)] min-h-[480px]"
            />
          )}
        </TabsContent>

        {/* O dicionário do AC16: com o que a skill vai de fato trabalhar. */}
        <TabsContent value="integrations" className="mt-6 space-y-4">
          {skill.integrations.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("skills.integrations.none")}</p>
          ) : (
            skill.integrations.map((integration) => (
              <div
                key={`${integration.requirement}:${integration.integration_base_id}`}
                className="space-y-2 rounded-md border p-4"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="text-sm font-medium">{integration.name}</p>
                  <span className="text-xs text-muted-foreground">
                    {requirementLabel(integration.requirement, t)}
                  </span>
                </div>

                <div className="space-y-1">
                  <p className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Wrench className="h-3 w-3" />
                    {t("skills.settings.tools.title", { count: integration.tools.length })}
                  </p>
                  {integration.tools.length === 0 ? (
                    <p className="text-xs text-muted-foreground/80">
                      {t("skills.settings.tools.none")}
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {integration.tools.map((tool) => (
                        <Badge key={tool} variant="outline" className="font-mono text-[11px]">
                          {tool}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}

          <p className="text-xs text-muted-foreground">{t("skills.settings.tools.hint")}</p>
        </TabsContent>

        {(skill.parameters?.length ?? 0) > 0 && (
          <TabsContent value="parameters" className="mt-6 space-y-4">
            {/* O MESMO componente do assistente de instalação: quem configura
                depois vê exatamente os campos que viu ao instalar, e duas
                renderizações do mesmo formulário divergiriam com o tempo. */}
            <ParametersStep
              parameters={skill.parameters.map((parametro) => ({
                ...parametro,
                // O passo pede `default`; aqui o valor gravado faz esse papel,
                // porque é ele que a instalação já usa.
                default: parametro.value,
              }))}
              values={rascunho ?? {}}
              onChange={(key, value) =>
                setRascunho((anteriores) => ({ ...(anteriores ?? {}), [key]: value }))
              }
            />

            {podeAdministrar && (
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={pendente || rascunho === null}
                  onClick={() => setRascunho(null)}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  size="sm"
                  disabled={pendente || rascunho === null}
                  onClick={() =>
                    agir(async () => {
                      // Grava o que está valendo mais o que foi mexido: mandar
                      // só o rascunho apagaria os que a pessoa não tocou.
                      const gravados = Object.fromEntries(
                        skill.parameters
                          .filter((parametro) => parametro.value !== null)
                          .map((parametro) => [parametro.key, parametro.value as string]),
                      );

                      await SkillsService.saveParameters(target, skill.name, {
                        ...gravados,
                        ...(rascunho ?? {}),
                      });

                      setRascunho(null);
                      toast({ title: t("skills.parameters.saved") });
                    }, "skills.updateFailed")
                  }
                >
                  {pendente ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  {t("skills.parameters.save")}
                </Button>
              </div>
            )}
          </TabsContent>
        )}

        {/* Versões: o histórico do PROCEDIMENTO. Versão publicada é imutável e
            nunca é reescrita, então esta lista é o que de fato mudou na skill —
            ao lado do histórico das instalações, que é outra pergunta. */}
        <TabsContent value="versions" className="mt-6 space-y-2">
          {/* Só até a versão INSTALADA. O que veio depois não faz parte desta
              instalação — o convite para avançar é o botão do cabeçalho, e
              listar aqui uma versão que não roda confundiria as duas coisas.
              O que sobra é exatamente o conjunto para onde dá para VOLTAR. */}
          {versoesDaInstalacao.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("skills.settings.versions.empty")}</p>
          ) : (
            versoesDaInstalacao.map((versao) => (
              <div
                key={versao.version}
                className="flex items-start justify-between gap-4 rounded-md border p-4"
              >
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium">
                      {t("skills.version", { version: versao.version })}
                    </p>
                    {versao.version === skill.version && (
                      <Badge variant="secondary" className="text-[11px]">
                        {t("skills.settings.versions.installed")}
                      </Badge>
                    )}
                    {versao.current && (
                      <Badge variant="outline" className="text-[11px]">
                        {t("skills.settings.versions.latest")}
                      </Badge>
                    )}
                  </div>
                  {textoDaSkill(versao.changelog, versao.changelog_i18n, i18n.language) && (
                    <p className="text-sm text-muted-foreground">
                      {textoDaSkill(versao.changelog, versao.changelog_i18n, i18n.language)}
                    </p>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-3">
                  <span className="text-xs text-muted-foreground">
                    {new Date(versao.published_at).toLocaleDateString()}
                  </span>

                  {/* Voltar é a mesma decisão de avançar, e passa pela mesma
                      prévia: a diferença é só o sentido da leitura. */}
                  {versao.version < skill.version && podeAdministrar && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 gap-1 text-muted-foreground"
                          disabled={pendente}
                          onClick={() => setAlvoDaTroca(versao.version)}
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                          {t("skills.update.rollback")}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        {t("skills.update.rollbackHint", { version: versao.version })}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </div>
            ))
          )}
        </TabsContent>

        {/* Histórico: o que PESSOAS fizeram com esta skill neste nível. Só ações
            de instalação por enquanto — o que um agente venha a relatar sobre
            execução é autodeclarado, e não pode dividir lista com isto. */}
        <TabsContent value="history" className="mt-6 space-y-2">
          {historico.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("skills.settings.history.empty")}</p>
          ) : (
            historico.map((linha, indice) => (
              <div
                key={`${linha.at}-${indice}`}
                className="flex items-center justify-between gap-4 rounded-md border p-4"
              >
                <div className="flex items-center gap-3">
                  {linha.action === "installed" ? (
                    <Download className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <Trash2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <div className="space-y-0.5">
                    <p className="text-sm">{t(`skills.settings.history.${linha.action}`)}</p>
                    {linha.version && (
                      <p className="text-xs text-muted-foreground">
                        {t("skills.version", { version: linha.version })}
                      </p>
                    )}
                  </div>
                </div>

                <span className="shrink-0 text-xs text-muted-foreground">
                  {new Date(linha.at).toLocaleString()}
                </span>
              </div>
            ))
          )}

          <p className="flex items-start gap-2 pt-2 text-xs text-muted-foreground">
            <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {t("skills.settings.history.hint")}
          </p>
        </TabsContent>

        {podeAdministrar && (
          <TabsContent value="security" className="mt-6">
            <Card className="border-destructive/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-destructive">
              {t("skills.settings.remove.title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center justify-between gap-3">
            <p className="max-w-md text-xs text-muted-foreground">
              {t("skills.settings.remove.hint")}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="gap-1 text-destructive hover:text-destructive"
              disabled={pendente}
              onClick={() =>
                agir(
                  async () => {
                    await SkillsService.uninstall(target, skill.name);
                    toast({
                      title: t("skills.uninstalled.title"),
                      description: t("skills.uninstalled.description", {
                        skill: skill.title || skill.name,
                      }),
                    });
                  },
                  "skills.uninstallFailed",
                  voltar,
                )
              }
            >
              {pendente ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
              {t("skills.uninstall")}
            </Button>
          </CardContent>
        </Card>
          </TabsContent>
        )}
      </Tabs>
      <SkillUpdateDialog
        open={alvoDaTroca !== null}
        onOpenChange={(aberto) => !aberto && setAlvoDaTroca(null)}
        target={target}
        name={skill.name}
        version={typeof alvoDaTroca === "number" ? alvoDaTroca : undefined}
        saving={pendente}
        onConfirm={() =>
          agir(async () => {
            const atualizada = await SkillsService.update(
              target,
              skill.name,
              typeof alvoDaTroca === "number" ? alvoDaTroca : undefined,
            );
            setAlvoDaTroca(null);
            toast({
              title: t("skills.updated.title"),
              description: t("skills.updated.description", {
                skill: skill.title || skill.name,
                version: atualizada.version,
              }),
            });
          }, "skills.updateFailed")
        }
      />
    </div>
  );
};

export default SkillSettings;
